import { Component, OnInit } from '@angular/core';
import { EntityService } from '../../services/entity.service';
import { Router } from '@angular/router';
import { Constants } from '../../models/constants.class';
import { CategorizerService } from '../../services/categorizer.service';
import { EntityInterface } from '../../models/entity.interface';
import { Location } from '@angular/common';
import { EntityProductType } from '../../models/entity.definitions';
import { TealiumService } from '@app/services/tealium.service';
import { ConfigService } from '@ng-darwin/config';
import { WindowMessagesService } from '@app/services/window-messages.service';

/**
 * @description Component
 * @class AgEntityComponent
 * @implements {OnInit} OnInit
 */
@Component({
  standalone: false,
  selector: 'app-ag-entity',
  templateUrl: './ag-entity.component.html',
  styleUrls: ['./ag-entity.component.scss']
})

export class AgEntityComponent implements OnInit {
  public entity: EntityInterface = {};
  public entityCode = '';
  public entitiesAggregated: Array<EntityInterface> = [];
  public constants = Constants;
  public interval: any;
  public expedientCode = '';
  public endSession = false;
  public aggregatedError = false;
  private _endPoints: any;

  /**
   * @description Creates an instance of AgEntityComponent.
   * @param router Router
   * @param entityService EntityService
   * @param categorizerService CategorizerService
   * @param _location Location
   * @param _tealiumService TealiumService
   * @param _configService ConfigService
   * @param _windowMessagesService WindowMessagesService
   */
  constructor(
    public readonly router: Router,
    public readonly entityService: EntityService,
    public readonly categorizerService: CategorizerService,
    private readonly _location: Location,
    private readonly _tealiumService: TealiumService,
    private readonly _configService: ConfigService,
    private readonly _windowMessagesService: WindowMessagesService
  ) {
    const appConfig = this._configService.config.app || {};
    this._endPoints = appConfig['endPoints'] || {};
  }

  /**
   * @description
   * @memberof AgEntityComponent
   */
  ngOnInit(): void {
    this.expedientCode = this.expedientCode = sessionStorage.getItem('prop-unique-id') as string;
    this.entity = this.entityService.getCategorizerEntity();
    if (Object.keys(this.entity).length === 0 || !this.expedientCode) {
      this.router.navigate(['']);
    }
    if (this.entity.status === Constants.statusNames.oncourse || this.entity.status === Constants.statusNames.validating) {
      this.setEntity(this.entity);
    }
    this._tealiumService.sendPageLoad({
      page_name: '/PSD2/agregar_banco/entidad_agregada/A1',
      page_title: 'PSD2 agregar banco entidad agregada A1',
      page_type: 'detalle',
      page_category: 'servicing',
      product_scope: 'operativa_no_financiera',
      product_category: 'PSD2',
      product_name: 'crédito digital',
      added_bank: this.entityService.getSelectedEntity().name,
    });
  }

  /**
   * @description addPointInteger
   * @param balance string default=''
   * @returns string
   */
  addPointInteger(balance = ''): string {
    return (
      balance
        .split('')
        .reverse()
        .map((digit, index) =>
          index !== 0 && index % 3 === 0 ? `${digit}.` : digit
        )
        .reverse()
        .join('')
    );
  }

  /**
   * @description getBalanceInteger
   * @param balance string
   * @returns string
   */
  getBalanceInteger(balance: string): string {
    return this.addPointInteger(balance.split('.').slice(0)[0]);
  }

  /**
   * @description getBalanceDecimal
   * @param balance string
   * @returns string
   */
  getBalanceDecimal(balance: string): string {
    return balance.split('.').slice(1)[0];
  }

  /**
   * @description asteriskIban
   * @param iban string
   * @returns string
   */
  asteriskIban(iban: string): string {
    return '*' + iban.slice(-4);
  }

  /**
   * @description back
   */
  back(): void {
    this._location.back();
  }

  /**
   * @description setEntity
   * @param entity EntityInterface
   */
  setEntity(entity: EntityInterface): void {
    const isOnlySantander: boolean = JSON.parse(sessionStorage.getItem('onlySantander')!) ?? false;
    this.interval = setInterval(() => {
      this.categorizerService.getEntities(this.expedientCode, isOnlySantander).then(entities => {
        const foundEntity: EntityInterface = entities.find(e => e.code === entity.code) || {};
        this.entityService.setSelectedEntity(entity);
        this.entityService.setCategorizerEntity(entity);
        this.entity = foundEntity;
        if (foundEntity.status === Constants.statusNames.ko || foundEntity.status === Constants.statusNames.ok) {
          clearInterval(this.interval);
        }
      }, () => {
        this._tealiumService.sendError({
          category: 'Finish error',
          action: 'warning',
          data: {
            cod_error: 400,
            desc_error: this._endPoints.categorizer,
          }
        });
        this.aggregatedError = true;
        clearInterval(this.interval);
      });
    }, 2000);
  }

  /**
   * @description continue
   */
  continue(): void {
    const products = this.entityService.getSelectedEntity().products || [];
    const hasPsd2Products = products.findIndex(product => product.type === EntityProductType.psd2);
    if (hasPsd2Products !== -1) {
      this.entityService.setSelectedEntityProductType(EntityProductType.psd2);
      Promise.resolve(this.router.navigate([Constants.url.terms]));
    }
  }

  /**
   * @description getBlackBankIcon
   * @param imageURL string
   * @returns string
   */
  getBlackBankIcon(imageURL: string): string {
    return imageURL.slice(0, imageURL.length - 4) + '-black.svg';
  }

  /**
   * @description isAnyAccountNotOwned
   * @returns boolean
   */
  isAnyAccountNotOwned(): boolean {
    return (this.entity.accounts || []).some(product => product.owner === false);
  }

  /**
   * @description goOutAppWithError
   */
  goOutAppWithError(): void {
    this._windowMessagesService.error();
    this.endSession = true;
  }
}
